/**
 * Aviso de versión nueva de la web, como el de la app de la clínica (y el de
 * Te Lo Presto). Al abrir la web, cada 10 minutos y al volver a la pestaña
 * mira /version.json (lo arma el build con las notas de version.ts). Si hay
 * una versión más nueva que la que está abierta, muestra una tarjeta ciruela
 * al medio con lo que trae, el aviso si lo hay, «Luego» y «Actualizar».
 * - «Actualizar» recarga la página.
 * - «Luego» la cierra hasta que se vuelva a abrir la web o salga otra más nueva.
 * - Si alguna de las nuevas es obligatoria, no se puede cerrar.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { CloudDownload, Download, Megaphone, Sparkles } from 'lucide-react-native';

import { APP_VERSION } from '@/version';
import { colors, typography } from '@/theme/tokens';

interface NotaRemota {
  version: string;
  titulo: string;
  cambios?: string[];
  aviso?: string;
  obligatoria?: boolean;
}

interface VersionRemota {
  version: string;
  notas?: NotaRemota[];
}

const MIRAR_CADA = 10 * 60 * 1000;
const ROSA = colors.magenta[200];
const BLANCO_70 = 'rgba(255,255,255,0.72)';
const BLANCO_8 = 'rgba(255,255,255,0.08)';

/** a es más nueva que b, número por número (1.10.0 > 1.9.3). */
function esMasNueva(a: string, b: string): boolean {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return false;
}

export function AvisoVersionNueva() {
  const [nueva, setNueva] = useState<VersionRemota | null>(null);
  const [cerradaPara, setCerradaPara] = useState<string | null>(null);
  const ultimaVez = useRef(0);

  const mirar = useCallback(async () => {
    // Al volver a la pestaña puede llamarse seguido: una vez por minuto alcanza.
    if (Date.now() - ultimaVez.current < 60_000) return;
    ultimaVez.current = Date.now();
    try {
      const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) return;
      const v = (await r.json()) as VersionRemota;
      if (typeof v?.version === 'string' && esMasNueva(v.version, APP_VERSION)) setNueva(v);
    } catch {
      // Sin internet (o sin version.json, en local): se vuelve a mirar más tarde.
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    mirar();
    const reloj = setInterval(mirar, MIRAR_CADA);
    const alVolver = () => {
      if (document.visibilityState === 'visible') mirar();
    };
    window.addEventListener('focus', alVolver);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      clearInterval(reloj);
      window.removeEventListener('focus', alVolver);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [mirar]);

  if (!nueva) return null;
  const nuevas = (nueva.notas ?? []).filter((n) => esMasNueva(n.version, APP_VERSION));
  const obligatoria = nuevas.some((n) => n.obligatoria);
  if (!obligatoria && cerradaPara === nueva.version) return null;

  return (
    <Cartel
      version={nueva.version}
      obligatoria={obligatoria}
      aviso={nuevas.find((n) => n.aviso)?.aviso}
      cambios={nuevas.flatMap((n) => (n.cambios?.length ? n.cambios : [n.titulo]))}
      alCerrar={() => setCerradaPara(nueva.version)}
    />
  );
}

function Cartel({
  version,
  obligatoria,
  aviso,
  cambios,
  alCerrar,
}: {
  version: string;
  obligatoria: boolean;
  aviso?: string;
  cambios: string[];
  alCerrar: () => void;
}) {
  const { height } = useWindowDimensions();
  return (
    <View
      style={{
        position: 'fixed' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100000,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        backgroundColor: obligatoria ? 'rgba(36,4,20,0.97)' : 'rgba(20,2,9,0.7)',
      }}
    >
      <View
        style={{
          width: '100%',
          maxWidth: 400,
          maxHeight: height - 40,
          borderRadius: 26,
          overflow: 'hidden',
          // @ts-ignore web only
          backgroundImage: `linear-gradient(160deg, ${colors.magenta[800]} 0%, ${colors.magenta[900]} 70%)`,
          shadowColor: '#000',
          shadowOpacity: 0.4,
          shadowRadius: 40,
          shadowOffset: { width: 0, height: 18 },
        }}
      >
        <ScrollView contentContainerStyle={{ paddingHorizontal: 22, paddingTop: 26, paddingBottom: 20 }}>
          <View style={{ alignItems: 'center' }}>
            <View
              style={{
                width: 66,
                height: 66,
                borderRadius: 33,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(248,201,223,0.16)',
              }}
            >
              <CloudDownload size={32} color={ROSA} strokeWidth={2.2} />
            </View>
            <Text
              style={{
                marginTop: 14,
                fontFamily: typography.display.fontFamily,
                fontSize: 22,
                fontWeight: typography.weights.bold,
                color: colors.white,
                textAlign: 'center',
              }}
            >
              {obligatoria ? 'Actualización requerida' : 'Hay una versión nueva'}
            </Text>
            <View style={{ marginTop: 8, paddingHorizontal: 11, paddingVertical: 4, borderRadius: 99, backgroundColor: BLANCO_8 }}>
              <Text style={{ fontSize: 12.5, fontWeight: typography.weights.extrabold, color: ROSA }}>
                Versión {version} · tenés la {APP_VERSION}
              </Text>
            </View>
            <Text style={{ marginTop: 10, fontSize: 14.5, lineHeight: 20, color: BLANCO_70, textAlign: 'center' }}>
              {obligatoria
                ? 'Para seguir usando CliniCAT necesitás actualizar a la última versión.'
                : 'Actualizá para tener lo último. Solo se recarga la página.'}
            </Text>
          </View>

          {aviso ? (
            <View
              style={{
                marginTop: 16,
                flexDirection: 'row',
                gap: 10,
                paddingVertical: 12,
                paddingHorizontal: 13,
                borderRadius: 16,
                backgroundColor: colors.warning[50],
              }}
            >
              <Megaphone size={18} color={colors.warning[600]} style={{ marginTop: 1 }} />
              <Text style={{ flex: 1, fontSize: 14.5, lineHeight: 20, fontWeight: typography.weights.bold, color: '#3D2300' }}>
                {aviso}
              </Text>
            </View>
          ) : null}

          {cambios.length > 0 ? (
            <View style={{ marginTop: 12, paddingTop: 13, paddingBottom: 9, paddingHorizontal: 15, borderRadius: 16, backgroundColor: BLANCO_8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                <Sparkles size={14} color={ROSA} />
                <Text style={{ fontSize: 11.5, fontWeight: typography.weights.extrabold, color: ROSA, letterSpacing: 1 }}>
                  QUÉ TRAE
                </Text>
              </View>
              {cambios.map((c, i) => (
                <View key={i} style={{ flexDirection: 'row', marginTop: 8 }}>
                  <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: ROSA, marginTop: 7, marginRight: 10 }} />
                  <Text style={{ flex: 1, fontSize: 14.5, lineHeight: 20, fontWeight: typography.weights.bold, color: colors.white }}>
                    {c}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          <View style={{ marginTop: 18, flexDirection: 'row', gap: 10 }}>
            {obligatoria ? null : (
              <Pressable
                onPress={alCerrar}
                style={({ hovered }: any) => ({
                  flex: 1,
                  height: 46,
                  borderRadius: 15,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 1.5,
                  borderColor: hovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.3)',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 15.5, fontWeight: typography.weights.bold, color: BLANCO_70 }}>
                  Luego
                </Text>
              </Pressable>
            )}
            <Pressable
              onPress={() => window.location.reload()}
              style={({ hovered }: any) => ({
                flex: 1,
                height: 46,
                borderRadius: 15,
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.success[600] : colors.success[500],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Download size={17} color={colors.white} strokeWidth={2.5} />
              <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 15.5, fontWeight: typography.weights.bold, color: colors.white }}>
                {obligatoria ? 'Actualizar ahora' : 'Actualizar'}
              </Text>
            </Pressable>
          </View>
        </ScrollView>
      </View>
    </View>
  );
}
